import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './RoughToPolishSection.css';
import '../common-styles.css';
import process1 from '../../assets/images/process/1.png';
import process2 from '../../assets/images/process/2.png';
import process3 from '../../assets/images/process/3.png';
import process4 from '../../assets/images/process/4.png';
import process5 from '../../assets/images/process/5.png';

interface ProcessCardData {
  id: number;
  step: number;
  title: string;
  subtitle?: string;
  description: string;
  image?: string; // image path placeholder
}

// NOTE: Text + images should be replaced with exact Figma copy/images.
// Provide image files under src/assets/rtp/ and update "image" fields.
const CARD_DATA: ProcessCardData[] = [
  {
    id: 0,
    step: 1,
    title: 'Discovery',
    subtitle: 'Unearthing natural rough diamonds from deep mines.',
  description: 'Every diamond begins its journey beneath the earth’s surface, formed over billions of years under immense heat and pressure. Miners carefully extract rough stones from deep rock layers or alluvial deposits. Each rough diamond is a unique creation of nature—raw, untouched, and full of potential. This step marks the start of the transformation from a hidden geological wonder to a symbol of brilliance and beauty.',
  image: process1
  },
  {
    id: 1,
    step: 2,
    title: 'Evaluation',
    subtitle: 'Assessing each stone’s quality to reveal its full potential.',
  description: 'Once unearthed, the rough diamond undergoes a detailed evaluation by expert gemologists. Factors such as carat weight, color, clarity, and internal structure are analyzed to determine the best approach for cutting. This critical phase ensures that each stone’s natural features are fully understood, guiding how it will be transformed to maximize beauty, brilliance, and overall value.',
  image: process2
  },
  {
    id: 2,
    step: 3,
    title: 'Planning',
    subtitle: 'Strategically mapping cuts to maximize brilliance and value.',
  description: 'The planning stage blends artistry with science. Using 3D modeling and advanced imaging, experts visualize the internal structure of the stone to plot the most precise and profitable cut. The goal is to enhance sparkle while preserving as much weight as possible. Every decision—from facet alignment to symmetry—is made with the final brilliance and value of the diamond in mind.',
  image: process3
  },
  {
    id: 3,
    step: 4,
    title: 'Cutting & Polishing',
    subtitle: 'Shaping and refining each facet for perfect light reflection.',
  description: 'This is where the rough stone transforms into a masterpiece. Skilled artisans carefully cut the diamond, often over many hours or even days. Each facet is shaped with exact angles to capture and reflect light flawlessly. Polishing brings out the final shine, creating that signature sparkle. It’s a combination of technical precision and human touch that unlocks the diamond’s full brilliance.',
  image: process4
  },
  {
    id: 4,
    step: 5,
    title: 'Certification & Presentation',
    subtitle: 'VERIFYING QUALITY AND SHOWCASING THE DIAMOND\'S FINAL BEAUTY.',
  description: 'After the diamond is fully polished, it is submitted to leading gemological laboratories—such as GIA or IGI—for grading and certification. Here, its cut, color, clarity, and carat weight are formally documented. Once certified, the diamond is presented in its final form, ready to be set in fine jewelry or cherished as a standalone gem—radiating elegance, authenticity, and timeless beauty.',
  image: process5
  }
];

// Base full height (desktop). Mobile variant handled via state for accurate stack container height.
const DESKTOP_CARD_FULL_HEIGHT = 330;
const MOBILE_CARD_FULL_HEIGHT = 600; // specified mobile full height
const PEEK_HEIGHT = 54; // ≈15% of full height visible for hidden cards

const RoughToPolishSection: React.FC = () => {
  // Default expanded last (step 5) per screenshot
  const [activeId, setActiveId] = useState<number>(4);

  // Reorder so active card renders last (expanded at bottom), others retain original order
  const ordered = useMemo(() => {
    const others = CARD_DATA.filter(c => c.id !== activeId);
    const active = CARD_DATA.find(c => c.id === activeId)!;
    return [...others, active];
  }, [activeId]);

  // Responsive card full height so the stacking container matches actual rendered card size.
  const [cardFullHeight, setCardFullHeight] = React.useState<number>(DESKTOP_CARD_FULL_HEIGHT);

  React.useEffect(() => {
    const applyHeight = () => {
      const w = window.innerWidth;
      // breakpoint aligns with mobile styling (<=768px)
      setCardFullHeight(w <= 991 ? MOBILE_CARD_FULL_HEIGHT : DESKTOP_CARD_FULL_HEIGHT);
    };
    applyHeight();
    window.addEventListener('resize', applyHeight);
    return () => window.removeEventListener('resize', applyHeight);
  }, []);

  const containerHeight = (CARD_DATA.length - 1) * PEEK_HEIGHT + cardFullHeight;

  return (
    <section className="section rough-to-polish-section">
      <Container>
        <Row className="section-top mb-5">
           <Col lg={12}>
                <div className="section-badge">ROUGH TO POLISH CYCLE</div>
           </Col>
          <Col lg={6}>
            <div className="section-header">
              <h2 className="section-title white-text">
                <span className="section-title-highlight">A meticulous cycle</span> that delivers clarity, value, and dazzling craftsmanship.
              </h2>
            </div>
          </Col>
          <Col lg={6}>
            <div className="section-info">
              <p className="section-description text-white">
                Behind every polished diamond is a story of transformation—a meticulous process that brings out unmatched clarity, symmetry, and sparkle.
              </p>
              <Link to="/process" className="section-btn" style={{ textDecoration: 'none' }}>
                <span className="btn-content">
                  VIEW PROCESS <i className="fas fa-arrow-right btn-arrow-diagonal" />
                </span>
              </Link>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div
              className="cards-stack-container"
              style={{ height: containerHeight }}
            >
              {ordered.map((card, i) => {
                const isActive = card.id === activeId;
                const top = i * PEEK_HEIGHT; // baseline vertical placement; collapsed transform lifts body leaving peek slice
                return (
                  <article
                    key={card.id}
                    className={`rtp-card ${isActive ? 'active' : 'collapsed'}`}
                    style={{ top, zIndex: 100 + i }}
                    aria-expanded={isActive}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveId(card.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveId(card.id); } }}
                  >
                    <div className="rtp-inner">
                      <div className="rtp-left">
                        <div className="rtp-header-row">
                          <h3 className="rtp-title">{card.step}. {card.title}</h3>
                        </div>
                        {card.subtitle && <div className={`rtp-subtitle-block ${isActive ? 'show' : ''}`}>{card.subtitle}</div>}
                        <p className={`rtp-description ${isActive ? 'show' : ''}`}>{card.description}</p>
                      </div>
                      <div className="rtp-right">
                        <div className="rtp-image-wrapper">
                          {card.image ? (
                            <img 
                              src={card.image} 
                              alt={card.title} 
                              className="rtp-image" 
                            />
                          ) : (
                            <div className="placeholder-image">
                              <i className="fas fa-gem" style={{ fontSize: '2rem', color: '#999' }} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {!isActive && <div className="rtp-gradient-fade" />}
                  </article>
                );
              })}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default RoughToPolishSection;
